import React, { useState, useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useTranslation } from "react-i18next";
import { getNestedValue, setNestedValue } from './util';
import DumbContainer from "@component/smartForm/DumbContainer"
import { areAllValidatorsValid } from "@component/smartForm/validatorUtils"

import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';

export const SmartFormStates = Object.freeze({
    EDIT: 'edit',
    VIEW: 'view',
    SAVING: 'saving', //on save button click 
    DELETE: 'delete',
})

// const FormSaveBlockator = {
//   validatorId: null,
//   isValid: true
// }

const SmartForm = ({
  formModel,
  setFormModel,
  isEdit: initialIsEdit = false,
  onSave,
  onCancel,
  onEdit,
  onDelete,
  children,
  contentSx = {},
  buttonPanelSx = {},
  isCreateOnly = false,
  isLoading = false,
  hasShowDeleteButton = false
}) => {
  const [isEdit, setIsEdit] = useState(initialIsEdit || isCreateOnly);
  const { t } = useTranslation("main");
  const [initFormModel, setInitFormModel] = useState(formModel);

  const [validatorResults, setValidatorResults] = useState({});
  const [formState, setFormState] = useState(isEdit ? SmartFormStates.EDIT : SmartFormStates.VIEW);

  useEffect(() => {
    if (!isEdit) {
      setInitFormModel(formModel);
      return;
    }
  }, [formModel, isEdit, initialIsEdit]);

  const handleInputChange = (field, value) => {
    setFormModel(prev => setNestedValue({ ...prev }, field, value));
  };

  const handleSave = () => {
    setFormState(SmartFormStates.SAVING)
    if (areAllValidatorsValid(validatorResults)) {
      console.log("form is valid")
      onSave?.(formModel);
      if (!isCreateOnly) {
        setIsEdit(false);
        setFormState(SmartFormStates.VIEW)
      }
    }
    else {
      console.log("form is not valid")
    }
  };

  const handleCancel = () => {
    setFormModel(initFormModel);
    onCancel?.(initFormModel);
    setIsEdit(false);
  };

  const handleEdit = () => {
    setFormState(SmartFormStates.EDIT)
    setIsEdit(true);
    onEdit?.();
  };

  const handleDelete = () => {
    setFormState(SmartFormStates.DELETE)
    onDelete?.();
  };

  const renderChildren = () => {
    return React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        if (child.type === DumbContainer)
          return child
        const value = child.props.value? child.props.value: getNestedValue(formModel, child.props.field);
        return (
          React.cloneElement(child, {
            model: formModel,
            value: value,
            onChange: handleInputChange,
            isEdit: isEdit,
            field: child.props.field,
            formState: formState,
            validatorResults: validatorResults,
            setValidatorResults: setValidatorResults
          })
        )
      }
      return child;
    });
  };

  return (
    <Box component="form" sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      gap: 0,
      position: 'relative' // Needed for absolute positioning of children
    }}>
      {/* Button panel - absolute positioned in top right corner */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        display: 'flex',
        gap: 1,
        zIndex: "tooltip", // Ensure buttons stay above other content
        ...buttonPanelSx
      }}>
        {isEdit ? (
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon fontSize="small" />}
              onClick={handleSave}
              disabled={isLoading}
            >
              {t("save")}
            </Button>
            {!isCreateOnly && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<ClearIcon fontSize="small" />}
                onClick={handleCancel}
              >
                {t("cancel")}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<EditIcon fontSize="small" />}
              onClick={handleEdit}
            >
              {t("edit")}
            </Button>
            {hasShowDeleteButton && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<DeleteIcon fontSize="small" />}
                onClick={handleDelete}
              >
                {t("delete")}
              </Button>
            )}
          </>
        )}
      </Box>

      {/* Content area - add padding top to prevent content hiding under buttons */}
      <Box sx={{ 
        display: "flex", 
        flexDirection: 'column', 
        gap: 2, 
        ...contentSx
      }}>
        {renderChildren()}
      </Box>
    </Box>
  );
};

export default SmartForm;